import { useState, useEffect, useCallback } from 'react'
import { ethers } from 'ethers'
import { parseAbi, formatEther, formatUnits } from 'viem'
import { executeWithRateLimit } from '../utils/rpcRateLimiter'
import { detachEthereumListeners, EthereumListenerHost } from '../utils/ethereumListeners'
import { logger } from '../utils/logger'
// AGC: potionInventory utils no longer needed - using on-chain getPlayerPotions view

// AGC: Default wallet address to use when no wallet is connected
const DEFAULT_WALLET_ADDRESS = '0x70997970C51812dc3A010C7d01b50e0d17dc79C8'

// AGC: Contract addresses - Read from environment variables with fallbacks
export const CONTRACT_ADDRESSES = {
  RNG_BEACON: import.meta.env.VITE_RNG_BEACON_ADDRESS || '0x0000000000000000000000000000000000000000', // AGC: RNG Beacon (not deployed in test)
  OGB_TOKEN: import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000',   // AGC: NEW unified OGB Token
  BUFF_1155: import.meta.env.VITE_BUFF_1155_ADDRESS || '0x0000000000000000000000000000000000000000',  // AGC: Buff1155 (ERC1155)
  CLASSIC_POTION_1155: import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000',  // AGC: ClassicPotion1155 (ERC1155)
  USDC: import.meta.env.VITE_USDC_ADDRESS || '0x0000000000000000000000000000000000000000',  // AGC: Official USDC on Base Sepolia (ERC20)
  // AGC: NEW - Crafting System Contracts (v6.8.0)
  CRAFT_MATERIAL_1155: import.meta.env.VITE_CRAFT_MATERIAL1155_ADDRESS || '0x0000000000000000000000000000000000000000', // AGC: CraftMaterial1155 (Herb, Crystal, Essence, Shard)
  CRAFTED_POTION_1155: import.meta.env.VITE_CRAFTED_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000', // AGC: CraftedPotion1155 (18 craftable potions)
} as const

// AGC: Debug log contract addresses on load
logger.debug('CONTRACT_ADDRESSES loaded:', {
  BUFF_1155: CONTRACT_ADDRESSES.BUFF_1155,
  OGB_TOKEN: CONTRACT_ADDRESSES.OGB_TOKEN,
  USDC: CONTRACT_ADDRESSES.USDC
})

// AGC: Get active RPC URL based on environment
// AGC: Now defaults to Base Sepolia since we're deployed there
const getActiveRpcUrl = (): string => {
  const network = import.meta.env.VITE_BASE_NETWORK || 'base-sepolia'
  const overrideGeneric = import.meta.env.VITE_BASE_RPC_URL
  const overrideSepolia = import.meta.env.VITE_BASE_SEPOLIA_RPC_URL
  const overrideMainnet = import.meta.env.VITE_BASE_MAINNET_RPC_URL

  if (network === 'base') {
    return overrideMainnet || overrideGeneric || 'https://mainnet.base.org'
  }

  if (network === 'base-sepolia') {
    return overrideSepolia || overrideGeneric || 'https://sepolia.base.org'
  }

  return overrideGeneric || 'http://127.0.0.1:8545' // Hardhat fallback
}

// AGC: Active RPC URL for the current environment
export const ACTIVE_RPC_URL = getActiveRpcUrl()
logger.debug('Active RPC URL selected for useContract:', ACTIVE_RPC_URL)

// AGC: Local Hardhat network configuration for development
export const LOCAL_NETWORK = {
  chainId: 31337, // Hardhat local network
  name: 'Hardhat Local',
  rpcUrl: 'http://127.0.0.1:8545',
  blockExplorer: 'http://localhost:8545' // No block explorer for local
}

// AGC: Base network configuration
export const BASE_NETWORK = {
  chainId: 8453, // Base Mainnet
  name: 'Base',
  rpcUrl: import.meta.env.VITE_BASE_MAINNET_RPC_URL || import.meta.env.VITE_BASE_RPC_URL || 'https://mainnet.base.org',
  blockExplorer: 'https://basescan.org'
}

// AGC: Base Testnet configuration for development
export const BASE_TESTNET = {
  chainId: 84532, // Base Sepolia Testnet
  name: 'Base Sepolia',
  rpcUrl: import.meta.env.VITE_BASE_SEPOLIA_RPC_URL || import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org',
  blockExplorer: 'https://sepolia.basescan.org'
}

// AGC: RNG Beacon contract ABI (essential functions only)
export const RNG_BEACON_ABI = [
  'function requestBattleRandomness(string memory matchId, uint256 turn) external returns (uint256 requestId)',
  'function getBattleRandom(string memory matchId, uint256 turn, string memory player, string memory component) external view returns (uint256)',
  'function getMatchData(string memory matchId) external view returns (tuple(string matchId, address playerA, address playerB, uint256 turn, uint256 randomSeed, bool isCompleted, address winner))',
  'event BattleRandomnessRequested(uint256 indexed requestId, string matchId, uint256 turn)',
  'event BattleRandomnessReceived(uint256 indexed requestId, string matchId, uint256 randomness)'
] as const

  // AGC: OGB Token contract ABI (essential functions only)
export const OGB_TOKEN_ABI = [
  'function balanceOf(address account) external view returns (uint256)',
  'function transfer(address to, uint256 amount) external returns (bool)',
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function transferFrom(address from, address to, uint256 amount) external returns (bool)',
  'function claimAirdrop() external',
  'function canPlayBattle(address player) external view returns (bool)',
  'function chargeBattleCost(address player) external',
  'function processBattleResult(address winner, address loser) external',
  'function processDrawResult(address player) external',
  'function processBattleLoss(address loser) external',
  'function processBattleDraw(address playerA, address playerB) external',
  'function burnTokens(uint256 amount) external',
  'function fundBattlePool(uint256 amount) external',
  'function hasReceivedAirdrop(address player) external view returns (bool)',
  'function getPlayerStatus(address player) external view returns (uint256 balance, bool canClaim, bool canBattle)',
  'function totalBurned() external view returns (uint256)',
  'function BATTLE_COST() external view returns (uint256)',
  'function AIRDROP_AMOUNT() external view returns (uint256)',
  'event AirdropClaimed(address indexed player, uint256 amount)',
  'event BattleCostCharged(address indexed player, uint256 amount)',
  'event BattleResultProcessed(address indexed winner, address indexed loser, uint256 winnerReward, uint256 burnAmount)',
  'event DrawResultProcessed(address indexed player, uint256 returnAmount, uint256 burnAmount)',
  'event BattleLossProcessed(address indexed loser, uint256 burnAmount)',
  'event BattleDrawProcessed(address indexed playerA, address indexed playerB, uint256 returnAmount, uint256 burnAmount)',
  'event TokensBurned(uint256 amount, uint256 totalBurned)',
  'event BattlePoolFunded(uint256 amount)',
  'event Transfer(address indexed from, address indexed to, uint256 value)'
] as const

// AGC: Buff1155 contract ABI (essential functions only)
// AGC: UPDATED (2025-11-27) - Added discount functions
export const BUFF_1155_ABI = parseAbi([
  'function balanceOf(address account, uint256 id) external view returns (uint256)',
  'function balanceOfBatch(address[] accounts, uint256[] ids) external view returns (uint256[] memory)',
  'function canUseBuffToday(address player, uint256 tokenId) external view returns (bool)',
  'function getAvailableBuffs(address player) external view returns (uint256[] memory)',
  'function markBuffUsed(address player, uint256 tokenId) external',
  'function getBuffProcRate(uint256 tokenId) external view returns (uint256)',
  'function getUsdPriceMicro(uint256 tokenId) external view returns (uint256)',
  'function getUsdPriceMicro(uint256 tokenId, address user) external view returns (uint256)',
  'function purchaseBuffWithETH(uint256 tokenId, uint256 amount) external payable',
  'function purchaseBuffWithOGB(uint256 tokenId, uint256 amount) external',
  'function purchaseBuffWithUSDC(uint256 tokenId, uint256 amount) external',
  'function getBuffPrices(uint256 tokenId) external view returns (uint256 ogbPrice, uint256 ethPrice, uint256 usdcPrice)',
  'function getBuffPricesForUser(uint256 tokenId, address user) external view returns (uint256 ogbPrice, uint256 ethPrice, uint256 usdcPrice)',
  'function hasMarketplaceDiscount(address user) external view returns (bool)',
  'function claimWelcomeBundle() external',
  'function hasClaimedWelcomeBundle(address player) external view returns (bool)',
  'function priceIncrementsPaused() external view returns (bool)',
  'function lifetimeMints(uint256 tokenId) external view returns (uint256)',
  'function assetQuotes() external view returns (uint256 ogbPerUsdMicro, uint256 ethPerUsdMicro, uint256 usdcPerUsdMicro, uint256 updatedAt)',
  'function usdcTokenAddress() external view returns (address)',
  'event BuffUsed(address indexed player, uint256 indexed tokenId, uint256 day)',
  'event BuffPurchased(address indexed buyer, uint256 indexed tokenId, uint256 amount, string paymentMethod, uint256 price)'
]) as const

// AGC: ClassicPotion1155 contract ABI (essential functions only)
export const CLASSIC_POTION_1155_ABI = [
  'function balanceOf(address account, uint256 id) external view returns (uint256)',
  'function purchasePotionWithETH(uint256 potionTypeId, uint8 farmPro) external payable',
  'function purchasePotionWithOGB(uint256 potionTypeId, uint8 farmPro) external',
  'function purchasePotionWithUSDC(uint256 potionTypeId, uint8 farmPro) external',
  'function getPotionPrices(address user) external view returns (uint256 ogbPrice, uint256 ethPrice, uint256 usdcPrice)',
  'function getPotionMetadata(uint256 tokenId) external view returns (uint256 potionTypeId, uint8 rarity, uint256 magnitude, uint256 mintedAt)',
  'function hasMarketplaceDiscount(address user) external view returns (bool)',
  'function getUsdPriceMicro(address user) external view returns (uint256)',
  // AGC: New on-chain ownership view (no more log scanning needed!)
  'function getPlayerPotions(address player) external view returns (uint256[])',
  'function getPlayerPotionBalances(address player) external view returns (uint256[] tokenIds, uint256[] balances)',
  'event PotionPurchased(address indexed buyer, uint256 indexed tokenId, uint256 potionType, uint8 rarity, string paymentMethod, uint256 price)',
  'event PotionMinted(address indexed to, uint256 indexed tokenId, uint256 potionType, uint8 rarity, uint256 magnitude)',
  'event TransferSingle(address indexed operator, address indexed from, address indexed to, uint256 id, uint256 value)'
] as const

// AGC: USDC contract ABI (ERC20 functions)
// AGC: Official USDC on Base Sepolia uses standard ERC20 interface
export const USDC_ABI = [
  'function balanceOf(address account) external view returns (uint256)',
  'function transfer(address to, uint256 amount) external returns (bool)',
  'function transferFrom(address from, address to, uint256 amount) external returns (bool)',
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function decimals() external view returns (uint8)',
  'function faucet() external',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)'
] as const

// AGC: CraftMaterial1155 contract ABI (NEW - v6.8.0)
// AGC: Crafting materials: 0=Herb, 1=Crystal, 2=Essence, 3=Shard
export const CRAFT_MATERIAL_1155_ABI = [
  'function balanceOf(address account, uint256 id) external view returns (uint256)',
  'function balanceOfBatch(address[] accounts, uint256[] ids) external view returns (uint256[])',
  'function getMaterialBalances(address account) external view returns (uint256 herb, uint256 crystal, uint256 essence, uint256 shard)',
  'function getMaterialInfo(uint256 materialId) external view returns (string name, string description, bool isActive, uint256 totalSupplyAmount)',
  'function setApprovalForAll(address operator, bool approved) external',
  'function isApprovedForAll(address account, address operator) external view returns (bool)',
  'event MaterialMinted(address indexed to, uint256 indexed materialId, uint256 amount)',
  'event MaterialBurned(address indexed from, uint256 indexed materialId, uint256 amount)'
] as const

// AGC: CraftedPotion1155 contract ABI (NEW - v6.8.0)
// AGC: All-in-one crafting: burn materials + pay OGB + mint potion
export const CRAFTED_POTION_1155_ABI = [
  'function craftPotion(uint256 potionTypeId, uint8 farmPro) external returns (uint256 tokenId)',
  'function getCraftingPrice(address user) external view returns (uint256 ogbPrice, bool hasDiscount, uint256 usdPriceMicro)',
  'function getRecipe(uint256 potionTypeId) external view returns (uint256[4] materialAmounts, uint256 baseMagnitude, uint8 tier, string name, bool isActive)',
  'function getPotionMetadata(uint256 tokenId) external view returns (uint256 potionTypeId, uint8 rarity, uint256 magnitude, uint256 mintedAt)',
  'function getPotionsOf(address owner) external view returns (uint256[])',
  'function canCraft(address user, uint256 potionTypeId) external view returns (bool canCraftFlag, uint256[4] missingMaterials, uint256 ogbNeeded, uint256 ogbBalance)',
  'function hasStakingDiscount(address user) external view returns (bool)',
  'function getRarityMultiplier(uint8 rarity) external pure returns (uint256)',
  'function balanceOf(address account, uint256 id) external view returns (uint256)',
  'function setApprovalForAll(address operator, bool approved) external',
  'function isApprovedForAll(address account, address operator) external view returns (bool)',
  'event PotionCrafted(address indexed crafter, uint256 indexed tokenId, uint256 potionTypeId, uint8 rarity, uint256 magnitude, uint256 ogbPaid)'
] as const

/**
 * AGC: Custom hook for interacting with Origami Battles smart contracts
 * AGC: Provides easy access to contract functions and state management
 */
export const useContract = () => {
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null)
  const [signer, setSigner] = useState<ethers.JsonRpcSigner | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  // AGC: Get current player address (connected wallet or default)
  const getCurrentPlayerAddress = useCallback(async (): Promise<string> => {
    if (signer) {
      return await signer.getAddress()
    }
    return DEFAULT_WALLET_ADDRESS
  }, [signer])

  useEffect(() => {
    // AGC: References to event handlers so we can detach them safely during cleanup.
    let accountsChangedHandler: ((accounts: string[]) => Promise<void>) | undefined
    let chainChangedHandler: ((chainId: string) => void) | undefined

    // AGC: Initialize provider when window.ethereum is available
    const initializeProvider = async () => {
      if (typeof window !== 'undefined' && window.ethereum) {
        try {
          const browserProvider = new ethers.BrowserProvider(window.ethereum)
          setProvider(browserProvider)
          
          // AGC: Listen for account changes
          accountsChangedHandler = async (accounts: string[]) => {
            logger.debug('MetaMask accounts changed:', accounts)
            if (accounts.length === 0) {
              // AGC: User disconnected
              setSigner(null)
              setIsConnected(false)
              logger.debug('Wallet disconnected')
            } else {
              // AGC: User switched accounts - reconnect with new account
              logger.debug('Switching to account:', accounts[0])
              try {
                const newSigner = await browserProvider.getSigner()
                const newAddress = await newSigner.getAddress()
                logger.info('Switched to new account:', newAddress)
                setSigner(newSigner)
                setIsConnected(true)
              } catch (error) {
                logger.error('Failed to switch account:', error)
              }
            }
          }
          window.ethereum.on('accountsChanged', accountsChangedHandler)

          // AGC: Listen for network changes
          chainChangedHandler = (chainId: string) => {
            logger.debug('Network changed to:', chainId)
            window.location.reload() // Reload page on network change
          }
          window.ethereum.on('chainChanged', chainChangedHandler)

          // AGC: Check if already connected and on correct network
          const accounts = await browserProvider.listAccounts()
          if (accounts.length > 0) {
            // AGC: Check network and connect
            const network = await browserProvider.getNetwork()
            logger.debug('Current network:', network.chainId, network.name)

            // AGC: Accept Hardhat (31337), Base Sepolia (84532), or Base Mainnet (8453)
            const validChainIds = [31337n, 84532n, 8453n]
            if (validChainIds.includes(network.chainId)) {
              const signer = await browserProvider.getSigner()
              setSigner(signer)
              setIsConnected(true)
              logger.info('Connected to network:', network.name, `(Chain ID: ${network.chainId})`)
            } else {
              logger.warn('Unsupported network. Please switch to Hardhat Local (31337), Base Sepolia (84532), or Base Mainnet (8453)')
            }
          }
        } catch (error) {
          logger.error('Failed to initialize provider:', error)
        }
      } else {
        logger.warn('MetaMask not detected. Please install MetaMask to connect your wallet.')
      }
    }

    initializeProvider()

    // AGC: Cleanup event listeners using the safest available removal API.
    return () => {
      if (typeof window === 'undefined') {
        return
      }

      detachEthereumListeners(window.ethereum as EthereumListenerHost | undefined, {
        accountsChanged: accountsChangedHandler,
        chainChanged: chainChangedHandler,
      })
    }
  }, [])

  // AGC: Connect wallet function
  const connectWallet = async () => {
    if (!provider) throw new Error('MetaMask not available. Please install MetaMask.')
    
    try {
      // AGC: Request account access
      logger.debug('Requesting account access from MetaMask...')
      await provider.send('eth_requestAccounts', [])

      // AGC: Check if we're on the correct network
      const network = await provider.getNetwork()
      logger.debug('Connected to network:', network.chainId, network.name)

      if (network.chainId !== 31337n) {
        // AGC: Try to switch to Hardhat local network
        try {
          await provider.send('wallet_switchEthereumChain', [
            { chainId: '0x7a69' } // 31337 in hex
          ])
        } catch (switchError: any) {
          // AGC: If network doesn't exist, add it
          if (switchError.code === 4902) {
            await provider.send('wallet_addEthereumChain', [
              {
                chainId: '0x7a69',
                chainName: 'Hardhat Local',
                nativeCurrency: {
                  name: 'Ethereum',
                  symbol: 'ETH',
                  decimals: 18,
                },
                rpcUrls: [ACTIVE_RPC_URL],
                blockExplorerUrls: null,
              },
            ])
          } else {
            throw switchError
          }
        }
      }

      const signer = await provider.getSigner()
      const address = await signer.getAddress()
      
      // AGC: Verify account ownership with signature
      logger.info('Requesting signature to verify account ownership...')
      const message = `Verify account ownership for Origami Battles\nAddress: ${address}\nTimestamp: ${Date.now()}`
      
      try {
        const signature = await signer.signMessage(message)
        logger.info('Account verified with signature:', signature.substring(0, 10) + '...')
      } catch (signError) {
        logger.warn('Signature verification skipped (user cancelled)')
        // AGC: Continue without signature - it's optional for local development
      }
      
      setSigner(signer)
      setIsConnected(true)
      logger.info('Wallet connected successfully to address:', address)
      return signer
    } catch (error) {
      logger.error('Failed to connect wallet:', error)
      throw error
    }
  }

  // AGC: Disconnect wallet function
  const disconnectWallet = () => {
    setSigner(null)
    setIsConnected(false)
  }

  // AGC: DEPRECATED - This was for local Hardhat testing only
  // AGC: DO NOT USE IN PRODUCTION - Function removed for security
  const addTestAccount = async () => {
    throw new Error('[ERROR] This function is deprecated. Use Base Sepolia testnet with real wallets.')
  }

  // AGC: Get RNG Beacon contract instance
  const getRngBeaconContract = () => {
    if (!signer) throw new Error('Wallet not connected')
    return new ethers.Contract(CONTRACT_ADDRESSES.RNG_BEACON, RNG_BEACON_ABI, signer)
  }

  // AGC: Get OGB Token contract instance
  const getOGBTokenContract = () => {
    if (!signer) throw new Error('Wallet not connected')
    logger.debug('Creating OGB contract with:', {
      address: CONTRACT_ADDRESSES.OGB_TOKEN,
      signerAddress: signer.address,
      provider: signer.provider?.connection?.url
    })
    return new ethers.Contract(CONTRACT_ADDRESSES.OGB_TOKEN, OGB_TOKEN_ABI, signer)
  }

  // AGC: Get Buff1155 contract instance
  const getBuff1155Contract = () => {
    if (!signer) throw new Error('Wallet not connected')
    return new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, BUFF_1155_ABI, signer)
  }

  // AGC: Get Buff1155 contract instance for read-only operations (no wallet required)
  const getBuff1155ContractReadOnly = () => {
    const provider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
    return new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, BUFF_1155_ABI, provider)
  }

  // AGC: Request battle randomness from RNG beacon
  const requestBattleRandomness = async (matchId: string, turn: number) => {
    try {
      const contract = getRngBeaconContract()
      const tx = await contract.requestBattleRandomness(matchId, turn)
      const receipt = await tx.wait()
      return receipt
    } catch (error) {
      logger.error('Failed to request battle randomness:', error)
      throw error
    }
  }

  // AGC: Get battle random value
  const getBattleRandom = async (
    matchId: string, 
    turn: number, 
    player: string, 
    component: string
  ): Promise<number> => {
    try {
      const contract = getRngBeaconContract()
      const randomValue = await contract.getBattleRandom(matchId, turn, player, component)
      return Number(randomValue)
    } catch (error) {
      logger.error('Failed to get battle random:', error)
      throw error
    }
  }

  // AGC: Get player's available buffs
  const getAvailableBuffs = async (playerAddress: string): Promise<number[]> => {
    try {
      // AGC: Try with wallet connection first, fallback to read-only for Direct Battle
      let contract
      try {
        // AGC: Check if we have a signer from useContract hook first
        if (signer) {
          contract = getBuff1155Contract()
        } else {
          // AGC: Try to get signer from browser wallet directly
          if (window.ethereum && window.ethereum.selectedAddress) {
            logger.debug('Using browser wallet signer for buffs...')
            const provider = new ethers.BrowserProvider(window.ethereum)
            const browserSigner = await provider.getSigner()
            contract = new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, BUFF_1155_ABI, browserSigner)
          } else {
            throw new Error('No wallet connection available')
          }
        }
      } catch (walletError) {
        logger.debug('Wallet not connected, using read-only contract for buffs...')
        contract = getBuff1155ContractReadOnly()
      }
      
      const buffs = await contract.getAvailableBuffs(playerAddress)
      return buffs.map((buff: any) => Number(buff))
    } catch (error) {
      logger.error('Failed to get available buffs:', error)
      throw error
    }
  }

  // AGC: Check if buff can be used today
  const canUseBuffToday = async (playerAddress: string, tokenId: number): Promise<boolean> => {
    try {
      // AGC: Try with wallet connection first, fallback to read-only for Direct Battle
      let contract
      try {
        // AGC: Check if we have a signer from useContract hook first
        if (signer) {
          contract = getBuff1155Contract()
        } else {
          // AGC: Try to get signer from browser wallet directly
          if (window.ethereum && window.ethereum.selectedAddress) {
            logger.debug('Using browser wallet signer for buff usage check...')
            const provider = new ethers.BrowserProvider(window.ethereum)
            const browserSigner = await provider.getSigner()
            contract = new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, BUFF_1155_ABI, browserSigner)
          } else {
            throw new Error('No wallet connection available')
          }
        }
      } catch (walletError) {
        logger.debug('Wallet not connected, using read-only contract for buff usage check...')
        contract = getBuff1155ContractReadOnly()
      }
      
      return await contract.canUseBuffToday(playerAddress, tokenId)
    } catch (error) {
      logger.error('Failed to check buff usage:', error)
      throw error
    }
  }

  // AGC: Mark buff as used (using authorized user wallet)
  const markBuffUsed = async (playerAddress: string, tokenId: number) => {
    try {
      if (!signer) {
        throw new Error('No signer available - please connect wallet')
      }

      const buff1155Abi = [
        'function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data)',
        'function balanceOf(address account, uint256 id) view returns (uint256)'
      ]
      
      const buff1155 = new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, buff1155Abi, signer)
      
      logger.debug(`Burning buff ${tokenId} for player ${playerAddress}`)
      
      // AGC: Check current balance before burning
      const balanceBefore = await buff1155.balanceOf(playerAddress, tokenId)
      logger.debug(`Balance before burning: ${balanceBefore}`)
      
      if (balanceBefore === 0n) {
        logger.warn(`Player has no buff ${tokenId} to burn`)
        return { success: false, message: 'No buff to burn' }
      }
      
      // AGC: Burn NFT by transferring to burn address (standard ERC1155 self-burn)
      // AGC: Players can burn their own NFTs without authorization
      const BURN_ADDRESS = '0x000000000000000000000000000000000000dEaD'
      logger.debug('Burning NFT by transferring to burn address...')
      const tx = await buff1155.safeTransferFrom(
        playerAddress,
        BURN_ADDRESS,
        tokenId,
        1, // Burn 1 NFT
        '0x' // Empty data
      )
      logger.info(`Transaction sent: ${tx.hash}`)
      logger.debug('Waiting for confirmation...')
      const receipt = await tx.wait()
      logger.info(`Transaction confirmed in block ${receipt.blockNumber}`)
      
      // AGC: Check balance after burning
      const balanceAfter = await buff1155.balanceOf(playerAddress, tokenId)
      logger.debug(`Balance after burning: ${balanceAfter}`)
      
      return { 
        success: true, 
        message: `Buff ${tokenId} burned successfully`,
        txHash: receipt.hash,
        balanceBefore: Number(balanceBefore),
        balanceAfter: Number(balanceAfter)
      }
    } catch (error) {
      logger.error('Failed to mark buff as used:', error)
      throw error
    }
  }

  // AGC: Get player's OGB token status
  const getPlayerOGBStatus = useCallback(async (playerAddress: string, forceRefresh = false) => {
    try {
      logger.debug('Getting player OGB status for:', playerAddress, forceRefresh ? '(force refresh)' : '')
      const contract = getOGBTokenContract()
      
      // AGC: Add debugging for contract call
      logger.debug('About to call contract.getPlayerStatus with:', {
        contractAddress: contract.target,
        playerAddress: playerAddress,
        signerConnected: !!signer
      })
      
      // AGC: Add a small delay if force refresh to ensure blockchain state is updated
      if (forceRefresh) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      
      const [balance, canClaim, canBattle] = await contract.getPlayerStatus(playerAddress)
      logger.debug('Raw contract response:', { balance: balance.toString(), canClaim, canBattle })
      const result = {
        balance: Number(ethers.formatEther(balance)),
        canClaim,
        canBattle,
        balanceWei: balance
      }
      logger.info('Player OGB status:', result)
      return result
    } catch (error) {
      logger.error('Failed to get player OGB status:', error)
      // AGC: Return default values if contract not deployed or network issues
      if (error.message?.includes('could not decode result data') ||
          error.message?.includes('call revert exception') ||
          error.message?.includes('network error')) {
        const fallback = {
          balance: 0,
          canClaim: true, // Allow claiming if contract not deployed
          canBattle: false,
          balanceWei: 0n
        }
        logger.warn('Using fallback OGB status:', fallback)
        return fallback
      }
      throw error
    }
  }, [signer])

  // AGC: Claim airdrop for new users
  const claimAirdrop = async () => {
    try {
      console.log(' Starting airdrop claim process...')
      const contract = getOGBTokenContract()
      console.log(' Got contract instance:', contract.target)

      // AGC: First try to estimate gas to see if the transaction would work
      console.log('• Estimating gas for claimAirdrop...')
      let gasEstimate
      try {
        gasEstimate = await contract.claimAirdrop.estimateGas()
        console.log('[OK] Gas estimate successful:', gasEstimate.toString())
      } catch (gasError) {
        console.error('[ERROR] Gas estimation failed:', gasError)
        throw new Error(`Gas estimation failed: ${gasError.message}`)
      }

      console.log(' Calling contract.claimAirdrop()...')
      
      // AGC: Check if MetaMask is responsive
      if (!signer) {
        throw new Error('No signer available. Please reconnect your wallet.')
      }
      
      // AGC: Try to get the current account to ensure MetaMask is active
      const currentAddress = await signer.getAddress()
      console.log(' Current signer address:', currentAddress)
      
      // AGC: Request MetaMask to be in foreground
      try {
        await window.ethereum.request({ method: 'eth_requestAccounts' })
      } catch (e) {
        console.warn('[WARN] Could not request accounts:', e.message)
      }
      
      // AGC: Add timeout to prevent hanging
      console.log(' Requesting transaction from MetaMask...')
      console.log(' Contract address:', contract.target)
      console.log(' Signer address:', currentAddress)
      
      // AGC: Try direct transaction approach first
      let tx
      try {
        console.log(' Attempting direct transaction approach...')
        const txData = contract.interface.encodeFunctionData('claimAirdrop', [])
        console.log(' Transaction data:', txData)
        
        const txRequest = {
          to: contract.target,
          data: txData,
          gasLimit: gasEstimate.toString()
        }
        
        console.log(' Transaction request:', txRequest)
        
        // AGC: Try using provider.send directly to force MetaMask popup
        console.log(' Trying provider.send method...')
        
        // AGC: Add timeout to provider.send
        const providerPromise = provider.send('eth_sendTransaction', [{
          from: currentAddress,
          to: contract.target,
          data: txData,
          gas: '0x' + gasEstimate.toString(16)
        }])
        
        const providerTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Provider.send timeout - MetaMask not responding')), 10000)
        )
        
        console.log('[WAIT] Waiting for provider.send response...')
        const txHash = await Promise.race([providerPromise, providerTimeout])
        console.log(' Provider.send succeeded!')
        
        console.log('[OK] Transaction sent via provider.send:', txHash)
        
        // AGC: Create transaction object for consistency
        tx = {
          hash: txHash,
          wait: () => provider.waitForTransaction(txHash)
        }
        
      } catch (directError) {
        console.warn('[WARN] Direct transaction failed, trying manual MetaMask request:', directError.message)
        
        // AGC: Try manual MetaMask request
        try {
          console.log(' Trying manual MetaMask eth_sendTransaction...')
          const txData = '0x5b88349d' // claimAirdrop() function selector
          const manualTxHash = await window.ethereum.request({
            method: 'eth_sendTransaction',
            params: [{
              from: currentAddress,
              to: contract.target,
              data: txData,
              gas: '0x' + gasEstimate.toString(16)
            }]
          })
          
          console.log('[OK] Manual transaction sent:', manualTxHash)
          tx = {
            hash: manualTxHash,
            wait: () => provider?.waitForTransaction(manualTxHash) || Promise.resolve(null)
          }
          
        } catch (manualError) {
          console.warn('[WARN] Manual transaction failed, trying contract method:', manualError.message)
          
          // AGC: Final fallback to contract method
          const txPromise = contract.claimAirdrop()
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Transaction timeout after 30 seconds - MetaMask popup may be blocked or hidden')), 30000)
          )
          
          console.log('[WAIT] Waiting for MetaMask popup and transaction response...')
          console.log(' If MetaMask popup doesn\'t appear, check if it\'s blocked or minimized')
          tx = await Promise.race([txPromise, timeoutPromise])
          console.log(' Transaction sent successfully!')
          console.log(' Transaction hash:', tx.hash)
        }
      }

      console.log('[WAIT] Waiting for transaction confirmation...')
      const receipt = await tx.wait()
      console.log('[OK] Transaction confirmed:', receipt.hash)

      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to claim airdrop:', error)
      // AGC: Provide helpful error messages for common issues
      if (error.message?.includes('could not decode result data')) {
        throw new Error('Contract not deployed. Please ensure the OGB Token contract is deployed to the network.')
      } else if (error.message?.includes('Already received airdrop') || error.message?.includes('Airdrop already claimed')) {
        throw new Error('You have already claimed your airdrop!')
      } else if (error.message?.includes('Airdrop pool empty') || error.message?.includes('Insufficient airdrop funds')) {
        throw new Error('Airdrop pool is empty. Please contact support.')
      } else if (error.code === 'ACTION_REJECTED') {
        throw new Error('Transaction was rejected by user.')
      }
      throw error
    }
  }

  // AGC: Check if player can play battle (has enough OGB)
  const canPlayBattle = async (playerAddress: string): Promise<boolean> => {
    try {
      const contract = getOGBTokenContract()
      return await contract.canPlayBattle(playerAddress)
    } catch (error) {
      console.error('Failed to check battle eligibility:', error)
      // AGC: Return false if contract not deployed or network issues
      if (error.message?.includes('could not decode result data') || 
          error.message?.includes('call revert exception') ||
          error.message?.includes('network error')) {
        return false
      }
      throw error
    }
  }

  // AGC: Charge battle cost (50 OGB) using transfer to contract
  const chargeBattleCost = async (playerAddress: string, onSuccess?: () => void) => {
    try {
      console.log(' Starting battle cost charge for:', playerAddress)
      const contract = getOGBTokenContract()
      
      // AGC: Get battle cost amount (50 OGB in wei)
      const battleCostWei = ethers.parseEther('50') // 50 OGB
      console.log(' Battle cost:', ethers.formatEther(battleCostWei), 'OGB')
      
      // AGC: Check if MetaMask is responsive
      if (!signer) {
        throw new Error('No signer available. Please reconnect your wallet.')
      }
      
      // AGC: Try to get the current account to ensure MetaMask is active
      const currentAddress = await signer.getAddress()
      console.log(' Current signer address:', currentAddress)
      
      // AGC: Force MetaMask to be active and responsive
      console.log(' Ensuring MetaMask is active...')
      try {
        // AGC: Multiple attempts to wake up MetaMask
        await window.ethereum.request({ method: 'eth_requestAccounts' })
        await window.ethereum.request({ method: 'eth_accounts' })
        
        // AGC: Try to get network info to ensure connection
        const chainId = await window.ethereum.request({ method: 'eth_chainId' })
        console.log(' Current chain ID:', chainId)
        
        // AGC: Small delay to ensure MetaMask is ready
        await new Promise(resolve => setTimeout(resolve, 500))
        
      } catch (e) {
        console.warn('[WARN] Could not activate MetaMask:', e.message)
      }
      
      // AGC: Use the most direct approach - window.ethereum.request
      console.log(' Using direct MetaMask request method...')
      
      const txData = contract.interface.encodeFunctionData('transfer', [contract.target, battleCostWei])
      console.log(' Transaction data:', txData)
      
      // AGC: Estimate gas first
      console.log('• Estimating gas for transfer...')
      const gasEstimate = await contract.transfer.estimateGas(contract.target, battleCostWei)
      console.log('[OK] Gas estimate successful:', gasEstimate.toString())
      
      console.log(' Requesting MetaMask transaction popup...')
      console.log(' From:', currentAddress)
      console.log(' To:', contract.target)
      console.log(' Amount:', ethers.formatEther(battleCostWei), 'OGB')
      
      // AGC: Prepare transaction parameters
      const txParams = {
        from: currentAddress,
        to: contract.target,
        data: txData,
        gas: '0x' + gasEstimate.toString(16),
        gasPrice: '0x' + (20000000000).toString(16) // 20 gwei
      }
      
      console.log(' Transaction parameters:', txParams)
      console.log(' MetaMask popup should appear now!')
      
      // AGC: Direct MetaMask request - this should always show the popup
      const txHash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [txParams]
      })
      
      console.log(' Transaction sent successfully!')
      console.log(' Transaction hash:', txHash)
      
      // AGC: Wait for transaction confirmation
      console.log('[WAIT] Waiting for transaction confirmation...')
      const receipt = await provider?.waitForTransaction(txHash)
      console.log('[OK] Battle cost charged successfully!')
      console.log(' Transaction receipt:', receipt)
      
      // AGC: Call refresh callback if provided
      if (onSuccess) {
        console.log(' Calling refresh callback...')
        onSuccess()
      }
      
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to charge battle cost:', error)
      
      // AGC: Provide helpful error messages
      if (error.message?.includes('insufficient funds') || error.message?.includes('transfer amount exceeds balance')) {
        throw new Error('You need at least 50 OGB tokens to play a battle!')
      } else if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new Error('Transaction was rejected by user.')
      } else if (error.message?.includes('could not decode result data')) {
        throw new Error('Contract not deployed. Please ensure the OGB Token contract is deployed.')
      } else if (error.message?.includes('User denied')) {
        throw new Error('Transaction was denied by user.')
      }
      
      throw error
    }
  }

  // AGC: Process battle result and distribute rewards
  const processBattleResult = async (winnerAddress: string, loserAddress: string) => {
    try {
      console.log('🔥 Processing battle result...')
      console.log(' Winner:', winnerAddress)
      console.log(' Loser:', loserAddress)
      
      const contract = getOGBTokenContract()
      
      // AGC: Estimate gas first
      console.log('• Estimating gas for processBattleResult...')
      const gasEstimate = await contract.processBattleResult.estimateGas(winnerAddress, loserAddress)
      console.log('[OK] Gas estimate successful:', gasEstimate.toString())
      
      console.log(' Requesting transaction to process battle result...')
      console.log(' Winner will receive: 75 OGB (50 + 25 bonus)')
      console.log(' Amount burned: 25 OGB')
      
      const tx = await contract.processBattleResult(winnerAddress, loserAddress)
      console.log(' Transaction sent:', tx.hash)
      
      console.log('[WAIT] Waiting for transaction confirmation...')
      const receipt = await tx.wait()
      console.log('[OK] Battle result processed successfully!')
      
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to process battle result:', error)
      
      // AGC: Provide helpful error messages
      if (error.message?.includes('Not authorized game server')) {
        throw new Error('Only authorized game servers can process battle results.')
      } else if (error.message?.includes('Loser insufficient balance')) {
        throw new Error('Loser does not have enough OGB tokens.')
      } else if (error.code === 'ACTION_REJECTED') {
        throw new Error('Transaction was rejected by user.')
      }
      
      throw error
    }
  }

  // AGC: Process draw result - return 25 OGB to player, burn 25 OGB
  const processDrawResult = async (playerAddress: string) => {
    try {
      console.log(' Processing draw result...')
      console.log(' Player:', playerAddress)
      
      const contract = getOGBTokenContract()
      
      // AGC: Estimate gas first
      console.log('• Estimating gas for processDrawResult...')
      const gasEstimate = await contract.processDrawResult.estimateGas(playerAddress)
      console.log('[OK] Gas estimate successful:', gasEstimate.toString())
      
      console.log(' Requesting transaction to process draw result...')
      console.log(' Player will receive: 25 OGB (refund)')
      console.log(' Amount burned: 25 OGB')
      
      const tx = await contract.processDrawResult(playerAddress)
      console.log(' Transaction sent:', tx.hash)
      
      console.log('[WAIT] Waiting for transaction confirmation...')
      const receipt = await tx.wait()
      console.log('[OK] Draw result processed successfully!')
      
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to process draw result:', error)
      
      // AGC: Provide helpful error messages
      if (error.message?.includes('Not authorized game server')) {
        throw new Error('Only authorized game servers can process draw results.')
      } else if (error.message?.includes('Insufficient contract balance')) {
        throw new Error('Contract does not have enough OGB tokens.')
      } else if (error.code === 'ACTION_REJECTED') {
        throw new Error('Transaction was rejected by user.')
      }
      
      throw error
    }
  }

  // AGC: Get total tokens burned (works without wallet connection)
  const getTotalBurned = async () => {
    // AGC: Helper to format OGB values with thousands separators (no trailing decimals)
    const formatOGB = (value: bigint) => {
      const parsed = Number.parseFloat(ethers.formatEther(value))
      if (!Number.isFinite(parsed)) {
        // AGC: Fallback to raw formatting if value is too large for Number
        return ethers.formatEther(value)
      }
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(parsed)
    }

    // AGC: Use rate limiter with 30-second cache to reduce RPC calls
    return executeWithRateLimit(
      'total-burned-tokens',
      async () => {
        console.log(' Getting total tokens burned...')
        
        // AGC: Use provider instead of signer for read-only operations
        const provider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.OGB_TOKEN, OGB_TOKEN_ABI, provider)

        // AGC: Verify contract code, but continue even if temporarily unavailable (due to rate limits)
        const contractCode = await provider.getCode(CONTRACT_ADDRESSES.OGB_TOKEN)
        if (contractCode === '0x') {
          console.warn('[WARN] OGB token contract code not found via RPC (possibly rate limiting) - attempting fallback anyway')
        }

        try {
          // AGC: Prefer native totalBurned() tracker for accuracy
          const totalBurnedValue: bigint = await contract.totalBurned()
          if (totalBurnedValue > 0n) {
            const formattedTotalBurned = formatOGB(totalBurnedValue)
            console.log('[OK] Total burned tokens (totalBurned):', formattedTotalBurned)
            return formattedTotalBurned
          }

          // AGC: Silently fall back - zero is expected for some contracts
        } catch {
          // AGC: totalBurned() not available on this contract version - silently use fallback
          // AGC: This is expected behavior, not a warning
        }
        
        // AGC: Fallback - Check balance of burn address (0xdead) when totalBurned() is unavailable or zero
        const burnAddress = '0x000000000000000000000000000000000000dEaD'
        const burnBalance: bigint = await contract.balanceOf(burnAddress)
        const formattedBurnBalance = formatOGB(burnBalance)
        console.log('[OK] Total burned tokens (burn address):', formattedBurnBalance)
        return formattedBurnBalance
      },
      { cacheDurationMs: 30000 } // AGC: Cache for 30 seconds
    )
  }

  // AGC: Test function for debugging MetaMask connectivity
  const testMetaMaskConnection = async () => {
    try {
      console.log(' Testing MetaMask connection...')
      
      if (!window.ethereum) {
        throw new Error('MetaMask not installed')
      }
      
      // AGC: Test basic connectivity
      const accounts = await window.ethereum.request({ method: 'eth_accounts' })
      console.log(' Available accounts:', accounts)
      
      const chainId = await window.ethereum.request({ method: 'eth_chainId' })
      console.log(' Chain ID:', chainId)
      
      // AGC: Test simple transaction (just a balance check)
      if (accounts.length > 0) {
        const balance = await window.ethereum.request({
          method: 'eth_getBalance',
          params: [accounts[0], 'latest']
        })
        console.log(' Balance:', balance)
      }
      
      console.log('[OK] MetaMask connection test passed!')
      return true
    } catch (error) {
      console.error('[ERROR] MetaMask connection test failed:', error)
      return false
    }
  }

  // AGC: Global refresh function for OGB status
  const refreshOGBStatus = useCallback(
    async (playerAddress?: string) => {
      const targetAddress = playerAddress || (await getCurrentPlayerAddress().catch(() => undefined))
      if (!targetAddress) {
        console.warn('⚠️ Refresh OGB status skipped – no wallet address available.')
        return
      }
      console.log(' Refreshing OGB status globally...')
      try {
        const status = await getPlayerOGBStatus(targetAddress)
        console.log('[OK] OGB Status refreshed:', status)
        return status
      } catch (error) {
        console.error('[ERROR] Failed to refresh OGB status:', error)
      }
    },
    [getCurrentPlayerAddress, getPlayerOGBStatus]
  )

  // AGC: ========== MARKETPLACE FUNCTIONS ==========

  // AGC: Get USDC contract instance
  const getUSDCContract = () => {
    if (!signer) throw new Error('Wallet not connected')
    return new ethers.Contract(CONTRACT_ADDRESSES.USDC, USDC_ABI, signer)
  }

  // AGC: Get buff prices for a specific buff type
  // AGC: UPDATED (2025-11-27) - Now supports user address for staking discount
  const getBuffPrices = async (tokenId: number, userAddress?: string) => {
    try {
      const contract = getBuff1155ContractReadOnly()
      
      let ogbPriceWei: bigint
      let ethPriceWei: bigint
      let usdcPriceBaseUnits: bigint
      let usdPriceMicro: bigint
      let hasDiscount = false
      
      // AGC: Try to get discounted prices if user address provided
      if (userAddress) {
        try {
          // AGC: Check if user has discount
          hasDiscount = await contract.hasMarketplaceDiscount(userAddress)
          
          // AGC: Get prices for user (includes discount if eligible)
          const [ogb, eth, usdc] = await contract.getBuffPricesForUser(tokenId, userAddress)
          ogbPriceWei = ogb as bigint
          ethPriceWei = eth as bigint
          usdcPriceBaseUnits = usdc as bigint
          
          // AGC: Get USD price with user discount
          usdPriceMicro = await contract['getUsdPriceMicro(uint256,address)'](tokenId, userAddress)
        } catch (discountError) {
          // AGC: Fallback to base prices if new functions not available (old contract)
          console.warn('Discount functions not available, using base prices:', discountError)
          const [ogb, eth, usdc] = await contract.getBuffPrices(tokenId)
          ogbPriceWei = ogb as bigint
          ethPriceWei = eth as bigint
          usdcPriceBaseUnits = usdc as bigint
          usdPriceMicro = await contract['getUsdPriceMicro(uint256)'](tokenId)
        }
      } else {
        // AGC: No user address, get base prices
        const [ogb, eth, usdc] = await contract.getBuffPrices(tokenId)
        ogbPriceWei = ogb as bigint
        ethPriceWei = eth as bigint
        usdcPriceBaseUnits = usdc as bigint
        usdPriceMicro = await contract['getUsdPriceMicro(uint256)'](tokenId)
      }

      const ogbPriceFormatted = ethers.formatEther(ogbPriceWei)
      const ethPriceFormatted = ethers.formatEther(ethPriceWei)
      const usdcPriceFormatted = ethers.formatUnits(usdcPriceBaseUnits, 6)
      const usdPriceFormatted = (Number(usdPriceMicro) / 1_000_000).toFixed(4)

      return {
        ogbPriceWei: ogbPriceWei as bigint,
        ethPriceWei: ethPriceWei as bigint,
        usdcPriceBaseUnits: usdcPriceBaseUnits as bigint,
        usdPriceMicro: Number(usdPriceMicro),
        ogbPrice: ogbPriceFormatted,
        ethPrice: ethPriceFormatted,
        usdcPrice: usdcPriceFormatted,
        usdPrice: usdPriceFormatted,
        hasDiscount: hasDiscount
      }
    } catch (error) {
      console.error('Failed to get buff prices:', error)
      throw error
    }
  }

  // AGC: Get buff information (static data for now)
  const getBuffInfo = async (tokenId: number) => {
    try {
      // AGC: Static buff information based on contract initialization
      const buffInfos = {
        0: { name: 'Shield', description: 'Reduces incoming damage by 25%', procRate: 100000, isActive: true },
        1: { name: 'Pierce', description: 'Reduces enemy defense effectiveness', procRate: 80000, isActive: true },
        2: { name: 'Haste', description: 'Adds +5 flat damage before defense', procRate: 50000, isActive: true },
        3: { name: 'Frost', description: 'Freezes opponent for one turn', procRate: 20000, isActive: true }
      }
      
      const info = buffInfos[tokenId as keyof typeof buffInfos]
      if (!info) {
        throw new Error('Invalid buff type')
      }
      
      return { 
        name: info.name, 
        description: info.description, 
        procRate: info.procRate, 
        isActive: info.isActive 
      }
    } catch (error) {
      console.error('Failed to get buff info:', error)
      throw error
    }
  }

  // AGC: Purchase buff with ETH
  const purchaseBuffWithETH = async (tokenId: number, amount: number) => {
    try {
      console.log(` Purchasing ${amount} buff(s) of type ${tokenId} with ETH...`)
      
      const contract = getBuff1155Contract()
      const buyerAddress = await signer!.getAddress()
      // AGC: FIX (2025-11-28) - Use user-aware pricing to get discounted price if eligible
      const prices = await getBuffPrices(tokenId, buyerAddress)
      if (!prices.ethPriceWei || prices.ethPriceWei === 0n) {
        throw new Error('AGC: Buff ETH price is not configured yet')
      }
      const totalPrice = prices.ethPriceWei * BigInt(amount)
      
      const tx = await contract.purchaseBuffWithETH(tokenId, amount, { value: totalPrice })
      console.log(' Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('[OK] Buff purchased with ETH successfully!')
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to purchase buff with ETH:', error)
      throw error
    }
  }

  // AGC: Purchase buff with OGB tokens
  const purchaseBuffWithOGB = async (tokenId: number, amount: number) => {
    try {
      console.log(` Purchasing ${amount} buff(s) of type ${tokenId} with OGB...`)
      
      const buffContract = getBuff1155Contract()
      const ogbContract = getOGBTokenContract()
      const buyerAddress = await signer!.getAddress()
      
      // AGC: FIX (2025-11-28) - Use user-aware pricing to get discounted price if eligible
      const prices = await getBuffPrices(tokenId, buyerAddress)
      if (!prices.ogbPriceWei || prices.ogbPriceWei === 0n) {
        throw new Error('AGC: Buff OGB price is not configured yet')
      }
      const totalPrice = prices.ogbPriceWei * BigInt(amount)
      
      // AGC: First approve the buff contract to spend OGB tokens
      console.log(' Approving OGB token spending...')
      const approveTx = await ogbContract.approve(CONTRACT_ADDRESSES.BUFF_1155, totalPrice)
      await approveTx.wait()
      
      // AGC: Then purchase the buff
      const tx = await buffContract.purchaseBuffWithOGB(tokenId, amount)
      console.log(' Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('[OK] Buff purchased with OGB successfully!')
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to purchase buff with OGB:', error)
      throw error
    }
  }

  // AGC: Approve OGB for crafting (approve backend wallet to spend OGB for potion crafting)
  const approveOGBForCrafting = async (amountWei: bigint) => {
    try {
      if (!signer) {
        throw new Error('AGC: Wallet not connected')
      }

      const ogbContract = getOGBTokenContract()
      const backendWalletAddress = import.meta.env.VITE_BACKEND_WALLET_ADDRESS || '0x8d9708e300E9DA93C288e2aB480F40479FD251aD'
      
      console.log(`🔐 Approving ${ethers.formatEther(amountWei)} OGB for crafting...`)
      console.log(`   Backend wallet: ${backendWalletAddress}`)
      
      // AGC: Check current allowance
      const userAddress = await signer.getAddress()
      const currentAllowance = await ogbContract.allowance(userAddress, backendWalletAddress)
      
      if (currentAllowance >= amountWei) {
        console.log('✅ Already approved - sufficient allowance')
        return { hash: 'already-approved' }
      }
      
      // AGC: Approve backend wallet to spend OGB
      const approveTx = await ogbContract.approve(backendWalletAddress, amountWei)
      console.log(`⏳ Approval transaction sent: ${approveTx.hash}`)
      
      await approveTx.wait()
      console.log('✅ OGB approved for crafting!')
      
      return approveTx
    } catch (error) {
      console.error('❌ Failed to approve OGB for crafting:', error)
      throw error
    }
  }

  // AGC: Purchase buff with USDC tokens
  const purchaseBuffWithUSDC = async (tokenId: number, amount: number) => {
    try {
      console.log(` Purchasing ${amount} buff(s) of type ${tokenId} with USDC...`)
      
      const buffContract = getBuff1155Contract()
      const buffContractReadOnly = getBuff1155ContractReadOnly()
      const usdcContract = getUSDCContract()
      const buyerAddress = await signer!.getAddress()
      
      // AGC: CRITICAL FIX (2025-12-01) - Verify USDC token address matches between frontend and contract
      const buffContractAddress = CONTRACT_ADDRESSES.BUFF_1155
      let contractUsdcAddress: string
      try {
        contractUsdcAddress = await buffContractReadOnly.usdcTokenAddress() as string
        console.log(` Contract's USDC token address: ${contractUsdcAddress}`)
        console.log(` Frontend's USDC token address: ${CONTRACT_ADDRESSES.USDC}`)
        
        // AGC: Normalize addresses for comparison (lowercase, no 0x prefix issues)
        const contractUsdcNormalized = contractUsdcAddress.toLowerCase()
        const frontendUsdcNormalized = CONTRACT_ADDRESSES.USDC.toLowerCase()
        
        if (contractUsdcNormalized !== frontendUsdcNormalized) {
          throw new Error(
            `USDC token address mismatch! Contract uses: ${contractUsdcAddress}, ` +
            `but frontend configured: ${CONTRACT_ADDRESSES.USDC}. ` +
            `Please update VITE_USDC_ADDRESS in env.public to match the contract.`
          )
        }
        console.log(` ✅ USDC token addresses match!`)
      } catch (addressError: any) {
        console.error('[ERROR] Failed to verify USDC token address:', addressError)
        // AGC: Continue anyway but warn - might be old contract without usdcTokenAddress function
        if (addressError.message?.includes('mismatch')) {
          throw addressError
        }
        console.warn('⚠️ Could not verify USDC address (contract might be old version), continuing...')
      }
      
      // AGC: CRITICAL FIX (2025-12-01) - Query contract DIRECTLY for exact price it will use
      // AGC: The contract calculates: unitPrice = getBuffPricesForUser(tokenId, msg.sender), totalPrice = unitPrice * amount
      // AGC: We must use the EXACT same calculation to avoid allowance mismatches
      console.log(` Querying contract for exact USDC price it will charge...`)
      let contractUnitPrice: bigint
      try {
        const [, , usdcPrice] = await buffContractReadOnly.getBuffPricesForUser(tokenId, buyerAddress)
        contractUnitPrice = usdcPrice as bigint
        console.log(` Contract unit price (USDC): ${contractUnitPrice.toString()}`)
      } catch (priceError) {
        console.warn('Failed to get user-specific price, falling back to base price:', priceError)
        const [, , usdcPrice] = await buffContractReadOnly.getBuffPrices(tokenId)
        contractUnitPrice = usdcPrice as bigint
        console.log(` Contract base unit price (USDC): ${contractUnitPrice.toString()}`)
      }
      
      // AGC: Calculate total price EXACTLY as the contract does: unitPrice * amount
      const contractTotalPrice = contractUnitPrice * BigInt(amount)
      console.log(` Contract will charge total: ${contractTotalPrice.toString()} USDC (${contractUnitPrice.toString()} × ${amount})`)
      
      if (contractTotalPrice === 0n) {
        throw new Error('AGC: Buff USDC price is not configured yet')
      }
      
      // AGC: Check existing allowance before sending an approval transaction
      console.log(` Checking USDC allowance for Buff1155: ${buffContractAddress}`)
      
      let currentAllowance = await usdcContract.allowance(buyerAddress, buffContractAddress)
      console.log(` Current allowance: ${currentAllowance.toString()}, Contract needs: ${contractTotalPrice.toString()}`)

      // AGC: Add 5% buffer to handle any edge cases (previous 1% wasn't enough for some scenarios)
      const priceWithBuffer = contractTotalPrice + (contractTotalPrice * 5n / 100n) // Add 5% buffer
      
      if (currentAllowance < contractTotalPrice) {
        console.log(
          ` Approving USDC token spending... (current allowance: ${currentAllowance.toString()}, contract needs: ${contractTotalPrice.toString()}, approving: ${priceWithBuffer.toString()})`
        )
        console.log(` Approving spender: ${buffContractAddress}`)
        
        try {
          // AGC: SECURITY FIX (2025-11-27) - Approve amount with buffer instead of unlimited
          // AGC: No unlimited authorizations - only approve what's needed for this purchase plus buffer
          const approveTx = await usdcContract.approve(buffContractAddress, priceWithBuffer)
          console.log(` Approval transaction sent: ${approveTx.hash}`)
          
          // AGC: Wait for approval to be mined
          const receipt = await approveTx.wait()
          console.log(`[OK] USDC allowance updated to ${priceWithBuffer.toString()} for Buff1155 contract (tx: ${receipt.hash})`)
          
          // AGC: CRITICAL - Verify allowance was actually updated on-chain with retries
          // AGC: Some RPC nodes may have propagation delays, especially for real USDC
          let verifiedAllowance = 0n
          let retryCount = 0
          const maxRetries = 5
          
          while (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 + (retryCount * 500))) // Increasing delay
            verifiedAllowance = await usdcContract.allowance(buyerAddress, buffContractAddress)
            console.log(` Allowance check attempt ${retryCount + 1}/${maxRetries}: ${verifiedAllowance.toString()}`)
            
            if (verifiedAllowance >= contractTotalPrice) {
              console.log(` ✅ Allowance verified successfully: ${verifiedAllowance.toString()}`)
              break
            }
            
            retryCount++
            if (retryCount < maxRetries) {
              console.log(` ⏳ Allowance not yet propagated, retrying in ${1 + (retryCount * 0.5)}s...`)
            }
          }
          
          currentAllowance = verifiedAllowance
          
          if (currentAllowance < contractTotalPrice) {
            throw new Error(
              `USDC allowance update failed after ${maxRetries} retries! ` +
              `Current: ${currentAllowance.toString()}, Required by contract: ${contractTotalPrice.toString()}. ` +
              `Please wait a few seconds and try again, or check the approval transaction: ${receipt.hash}`
            )
          }
        } catch (approveError: any) {
          console.error('[ERROR] Failed to approve USDC:', approveError)
          
          // AGC: Handle user rejection gracefully
          if (approveError?.code === 'ACTION_REJECTED' || approveError?.code === 4001) {
            throw new Error('USDC approval was rejected. Please approve USDC spending to complete the purchase.')
          }
          
          throw new Error(`USDC approval failed: ${approveError instanceof Error ? approveError.message : 'Unknown error'}`)
        }
      } else {
        console.log(`[INFO] Existing USDC allowance (${currentAllowance.toString()}) sufficient for contract price (${contractTotalPrice.toString()}), skipping approve transaction`)
      }
      
      // AGC: CRITICAL - Final allowance check right before purchase using contract's exact price
      const finalAllowance = await usdcContract.allowance(buyerAddress, buffContractAddress)
      console.log(` Final allowance check before purchase: ${finalAllowance.toString()}, Contract will charge: ${contractTotalPrice.toString()}`)
      
      if (finalAllowance < contractTotalPrice) {
        throw new Error(`Insufficient USDC allowance! Current: ${finalAllowance.toString()}, Contract needs: ${contractTotalPrice.toString()}. Please approve USDC spending first.`)
      }
      
      // AGC: Double-check by querying contract price one more time right before purchase (in case price changed)
      const [, , finalPriceCheck] = await buffContractReadOnly.getBuffPricesForUser(tokenId, buyerAddress)
      const finalUnitPriceCheck = finalPriceCheck as bigint
      const finalTotalPriceCheck = finalUnitPriceCheck * BigInt(amount)
      
      if (finalTotalPriceCheck !== contractTotalPrice) {
        console.warn(`⚠️ Price changed between checks! Original: ${contractTotalPrice.toString()}, Now: ${finalTotalPriceCheck.toString()}`)
        if (finalAllowance < finalTotalPriceCheck) {
          throw new Error(`Price changed! Contract now needs: ${finalTotalPriceCheck.toString()}, but allowance is: ${finalAllowance.toString()}. Please try again.`)
        }
      }
      
      // AGC: CRITICAL - Estimate gas first to catch any issues before sending the real transaction
      // AGC: This will catch allowance issues during gas estimation
      console.log(` Estimating gas for purchase transaction...`)
      try {
        const gasEstimate = await buffContract.purchaseBuffWithUSDC.estimateGas(tokenId, amount)
        console.log(` ✅ Gas estimation succeeded (${gasEstimate.toString()} gas) - purchase should work!`)
      } catch (gasError: any) {
        console.error('[ERROR] Gas estimation failed - purchase will fail:', gasError)
        
        // AGC: Provide helpful error messages
        if (gasError.message?.includes('insufficient allowance') || gasError.reason?.includes('insufficient allowance')) {
          // AGC: Re-check allowance one more time with fresh data
          const lastAllowanceCheck = await usdcContract.allowance(buyerAddress, buffContractAddress)
          const lastPriceCheck = await (async () => {
            try {
              const [, , price] = await buffContractReadOnly.getBuffPricesForUser(tokenId, buyerAddress)
              return (price as bigint) * BigInt(amount)
            } catch {
              return contractTotalPrice
            }
          })()
          
          throw new Error(
            `USDC allowance insufficient for purchase! ` +
            `Current allowance: ${lastAllowanceCheck.toString()}, ` +
            `Required: ${lastPriceCheck.toString()}. ` +
            `The approval transaction may not have propagated to all RPC nodes yet. ` +
            `Please wait 10-15 seconds and try again, or manually check your USDC allowance on BaseScan: ` +
            `https://sepolia.basescan.org/address/${CONTRACT_ADDRESSES.USDC}#readProxyContract`
          )
        }
        
        throw gasError
      }
      
      // AGC: Then purchase the buff
      console.log(` Attempting purchase with allowance: ${finalAllowance.toString()}, contract will charge: ${contractTotalPrice.toString()}`)
      const tx = await buffContract.purchaseBuffWithUSDC(tokenId, amount)
      console.log(' Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('[OK] Buff purchased with USDC successfully!')
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to purchase buff with USDC:', error)
      throw error
    }
  }

  // AGC: Get player's USDC balance
  const getUSDCBalance = async (playerAddress: string) => {
    try {
      // AGC: Check if USDC address is configured
      if (!CONTRACT_ADDRESSES.USDC || CONTRACT_ADDRESSES.USDC === '0x0000000000000000000000000000000000000000') {
        console.warn('⚠️ USDC contract address not configured')
        return '0'
      }
      
      // AGC: Use provider for read-only operations (no wallet needed)
      const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
      const contract = new ethers.Contract(CONTRACT_ADDRESSES.USDC, USDC_ABI, readProvider)
      const balance = await contract.balanceOf(playerAddress)
      return ethers.formatUnits(balance, 6) // USDC has 6 decimals
    } catch (error) {
      console.error('Failed to get USDC balance:', error)
      return '0' // AGC: Return 0 instead of throwing to prevent UI errors
    }
  }

  // AGC: Get Classic Potion contract instance
  const getClassicPotionContract = () => {
    if (!signer) {
      throw new Error('AGC: Wallet not connected')
    }
    if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
      throw new Error('AGC: Classic Potion contract address not configured')
    }
    return new ethers.Contract(CONTRACT_ADDRESSES.CLASSIC_POTION_1155, CLASSIC_POTION_1155_ABI, signer)
  }

  // AGC: Get classic potion prices
  const getClassicPotionPrices = async (userAddress: string) => {
    try {
      const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
      const contract = new ethers.Contract(CONTRACT_ADDRESSES.CLASSIC_POTION_1155, CLASSIC_POTION_1155_ABI, readProvider)
      const [ogbPrice, ethPrice, usdcPrice] = await contract.getPotionPrices(userAddress)
      const hasDiscount = await contract.hasMarketplaceDiscount(userAddress)
      const usdPriceMicro = await contract.getUsdPriceMicro(userAddress) // AGC: Get USD price (takes user address, not tokenId)
      const baseUsdPrice = Number(usdPriceMicro) / 1_000_000 // AGC: Convert from micro-dollars
      
      return {
        ogbPriceWei: ogbPrice as bigint,
        ethPriceWei: ethPrice as bigint,
        usdcPriceBaseUnits: usdcPrice as bigint,
        ogbPrice: formatEther(ogbPrice as bigint),
        ethPrice: formatEther(ethPrice as bigint),
        usdcPrice: formatUnits(usdcPrice as bigint, 6),
        hasDiscount: hasDiscount as boolean,
        baseUsdPrice: baseUsdPrice
      }
    } catch (error) {
      console.error('Failed to get classic potion prices:', error)
      throw error
    }
  }

  // AGC: Purchase classic potion with ETH
  const purchaseClassicPotionWithETH = async (potionType: number, farmPro: number) => {
    try {
      if (!signer) {
        throw new Error('AGC: Wallet not connected')
      }
      
      const contract = getClassicPotionContract()
      const userAddress = await signer.getAddress()
      
      // AGC: Get price
      const prices = await getClassicPotionPrices(userAddress)
      const totalPrice = prices.ethPriceWei
      
      // AGC: Convert FarmPro (0-1) to 0-100 scale
      const farmProScaled = Math.round(farmPro * 100)
      
      console.log(`🔮 Purchasing classic potion type ${potionType} with ETH (FarmPro: ${farmProScaled})...`)
      const tx = await contract.purchasePotionWithETH(potionType, farmProScaled, { value: totalPrice })
      console.log('⏳ Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('✅ Classic potion purchased with ETH!')
      
      // AGC: Parse PotionPurchased event from receipt
      const potionData = parsePotionPurchasedEvent(receipt)
      return { receipt, potionData }
    } catch (error) {
      console.error('❌ Failed to purchase classic potion with ETH:', error)
      throw error
    }
  }

  // AGC: Purchase classic potion with OGB
  const purchaseClassicPotionWithOGB = async (potionType: number, farmPro: number) => {
    try {
      if (!signer) {
        throw new Error('AGC: Wallet not connected')
      }
      
      const contract = getClassicPotionContract()
      const userAddress = await signer.getAddress()
      
      // AGC: Get price
      const prices = await getClassicPotionPrices(userAddress)
      const totalPrice = prices.ogbPriceWei
      
      // AGC: Approve OGB spending
      const ogbContract = getOGBTokenContract()
      const currentAllowance = await ogbContract.allowance(userAddress, CONTRACT_ADDRESSES.CLASSIC_POTION_1155)
      
      if (currentAllowance < totalPrice) {
        // AGC: SECURITY FIX (2025-11-27) - Approve exact amount instead of unlimited
        // AGC: No unlimited authorizations - only approve what's needed for this purchase
        console.log('🔓 Approving OGB spending (exact amount)...')
        const approveTx = await ogbContract.approve(CONTRACT_ADDRESSES.CLASSIC_POTION_1155, totalPrice)
        await approveTx.wait()
        console.log('✅ OGB approved for exact purchase amount')
      }
      
      // AGC: Convert FarmPro (0-1) to 0-100 scale
      const farmProScaled = Math.round(farmPro * 100)
      
      console.log(`🔮 Purchasing classic potion type ${potionType} with OGB (FarmPro: ${farmProScaled})...`)
      const tx = await contract.purchasePotionWithOGB(potionType, farmProScaled)
      console.log('⏳ Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('✅ Classic potion purchased with OGB!')
      
      // AGC: Parse PotionPurchased event from receipt
      const potionData = parsePotionPurchasedEvent(receipt)
      return { receipt, potionData }
    } catch (error) {
      console.error('❌ Failed to purchase classic potion with OGB:', error)
      throw error
    }
  }

  // AGC: Purchase classic potion with USDC
  const purchaseClassicPotionWithUSDC = async (potionType: number, farmPro: number) => {
    try {
      if (!signer) {
        throw new Error('AGC: Wallet not connected')
      }
      
      const userAddress = await signer.getAddress()
      console.log('🔍 [USDC Purchase] User address:', userAddress)
      console.log('🔍 [USDC Purchase] Contract address:', CONTRACT_ADDRESSES.CLASSIC_POTION_1155)
      
      // AGC: Get price
      const prices = await getClassicPotionPrices(userAddress)
      const totalPrice = prices.usdcPriceBaseUnits
      console.log('🔍 [USDC Purchase] Total price:', totalPrice.toString())
      
      // AGC: Approve USDC spending
      const usdcContract = getUSDCContract()
      const currentAllowance = await usdcContract.allowance(userAddress, CONTRACT_ADDRESSES.CLASSIC_POTION_1155)
      console.log('🔍 [USDC Purchase] Current allowance:', currentAllowance.toString())
      
      let justApproved = false
      if (currentAllowance < totalPrice) {
        // AGC: SECURITY FIX (2025-11-27) - Approve exact amount instead of unlimited
        // AGC: No unlimited authorizations - only approve what's needed for this purchase
        console.log('🔓 Approving USDC spending (exact amount)...')
        const approveTx = await usdcContract.approve(CONTRACT_ADDRESSES.CLASSIC_POTION_1155, totalPrice)
        await approveTx.wait()
        console.log('✅ USDC approved for exact purchase amount')
        justApproved = true
        
        // AGC: FIX - Wait for approval to propagate across nodes
        console.log('⏳ Waiting for approval to propagate...')
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
      
      // AGC: FIX - Re-verify allowance if we just approved
      if (justApproved) {
        const verifiedAllowance = await usdcContract.allowance(userAddress, CONTRACT_ADDRESSES.CLASSIC_POTION_1155)
        console.log('🔍 [USDC Purchase] Verified allowance after approval:', verifiedAllowance.toString())
        if (verifiedAllowance < totalPrice) {
          throw new Error('Approval not yet confirmed. Please try again in a few seconds.')
        }
      }
      
      // AGC: Convert FarmPro (0-1) to 0-100 scale and ensure it's uint8 (0-255)
      const farmProScaled = Math.min(Math.max(Math.round(farmPro * 100), 0), 255)
      console.log('🔍 [USDC Purchase] FarmPro scaled:', farmProScaled)
      
      // AGC: Get contract instance with signer
      const contract = getClassicPotionContract()
      console.log('🔍 [USDC Purchase] Contract instance created')
      
      // AGC: Verify function exists
      if (!contract.purchasePotionWithUSDC) {
        throw new Error('AGC: purchasePotionWithUSDC function not found in contract ABI')
      }
      
      console.log(`🔮 Purchasing classic potion type ${potionType} with USDC (FarmPro: ${farmProScaled})...`)
      
      // AGC: Verify USDC balance is sufficient
      const usdcContractForBalance = getUSDCContract()
      const balance = await usdcContractForBalance.balanceOf(userAddress)
      console.log('🔍 [USDC Purchase] User USDC balance:', ethers.formatUnits(balance, 6))
      if (balance < totalPrice) {
        throw new Error(`Insufficient USDC balance. Have: ${ethers.formatUnits(balance, 6)}, Need: ${ethers.formatUnits(totalPrice, 6)}`)
      }
      
      // AGC: FIX - Estimate gas and add 30% buffer to prevent out-of-gas failures
      let gasLimit: bigint
      try {
        const gasEstimate = await contract.purchasePotionWithUSDC.estimateGas(potionType, farmProScaled)
        gasLimit = (gasEstimate * 130n) / 100n // AGC: Add 30% buffer
        console.log('🔍 [USDC Purchase] Gas estimate:', gasEstimate.toString(), '+ 30% buffer =', gasLimit.toString())
      } catch (estimateError: any) {
        console.error('❌ [USDC Purchase] Gas estimation failed:', estimateError.message)
        // AGC: Use fallback gas limit if estimation fails
        gasLimit = 350000n
        console.log('🔍 [USDC Purchase] Using fallback gas limit:', gasLimit.toString())
      }
      
      // AGC: Call contract function with explicit gas limit
      const tx = await contract.purchasePotionWithUSDC(potionType, farmProScaled, {
        gasLimit: gasLimit
      })
      console.log('⏳ Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('✅ Classic potion purchased with USDC!')
      
      // AGC: Parse PotionPurchased event from receipt
      const potionData = parsePotionPurchasedEvent(receipt)
      return { receipt, potionData }
    } catch (error: any) {
      console.error('❌ Failed to purchase classic potion with USDC:', error)
      // AGC: Provide more helpful error message
      if (error.message?.includes('insufficient')) {
        throw new Error('Insufficient USDC balance or allowance. Please check your wallet.')
      }
      if (error.message?.includes('user rejected') || error.code === 'ACTION_REJECTED') {
        throw new Error('Transaction was rejected by user.')
      }
      if (error.message?.includes('reverted')) {
        throw new Error('Transaction reverted. This can happen due to network congestion. Please try again.')
      }
      throw error
    }
  }

  // AGC: Parse PotionPurchased event from transaction receipt
  const parsePotionPurchasedEvent = (receipt: any) => {
    try {
      if (!receipt || !receipt.logs) return null

      const contract = getClassicPotionContract()
      const eventInterface = contract.interface

      // AGC: Find PotionPurchased event in logs
      for (const log of receipt.logs) {
        try {
          const parsed = eventInterface.parseLog({
            topics: log.topics,
            data: log.data
          })
          
          if (parsed && parsed.name === 'PotionPurchased') {
            return {
              tokenId: Number(parsed.args.tokenId),
              potionType: Number(parsed.args.potionType),
              rarity: Number(parsed.args.rarity),
              paymentMethod: parsed.args.paymentMethod,
              price: parsed.args.price,
              txHash: receipt.hash
            }
          }
        } catch (e) {
          // AGC: Not the event we're looking for, continue
          continue
        }
      }

      // AGC: If PotionPurchased not found, try to get from PotionMinted event
      for (const log of receipt.logs) {
        try {
          const parsed = eventInterface.parseLog({
            topics: log.topics,
            data: log.data
          })
          
          if (parsed && parsed.name === 'PotionMinted') {
            // AGC: Get magnitude from metadata
            return {
              tokenId: Number(parsed.args.tokenId),
              potionType: Number(parsed.args.potionType),
              rarity: Number(parsed.args.rarity),
              magnitude: Number(parsed.args.magnitude),
              txHash: receipt.hash
            }
          }
        } catch (e) {
          continue
        }
      }

      return null
    } catch (error) {
      console.error('Failed to parse PotionPurchased event:', error)
      return null
    }
  }

  // AGC: Claim USDC from faucet (for testing)
  const claimUSDCFaucet = async () => {
    try {
      console.log(' Claiming USDC from faucet...')
      const contract = getUSDCContract()
      const tx = await contract.faucet()
      console.log(' Transaction sent:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('[OK] USDC claimed from faucet successfully!')
      return receipt
    } catch (error) {
      console.error('[ERROR] Failed to claim USDC from faucet:', error)
      throw error
    }
  }

  // AGC: Get player's buff NFT balances
  const getPlayerBuffBalances = async (playerAddress: string) => {
    // AGC: Use rate limiter with 15-second cache per player
    return executeWithRateLimit(
      `buff-balances-${playerAddress}`,
      async () => {
        console.log(' Getting player buff balances...')
        console.log('   Player:', playerAddress)
        console.log('   Buff1155 Contract:', CONTRACT_ADDRESSES.BUFF_1155)
        console.log('   RPC URL:', ACTIVE_RPC_URL)
        
        // AGC: Check if contract address is valid
        if (CONTRACT_ADDRESSES.BUFF_1155 === '0x0000000000000000000000000000000000000000') {
          throw new Error('BUFF_1155 contract address not set in environment variables')
        }
        
        // AGC: Use provider for read-only operations (no wallet needed)
        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.BUFF_1155, BUFF_1155_ABI, readProvider)
        const buffIds = [0, 1, 2, 3] // SHIELD, PIERCE, HASTE, FROST
        const addresses = Array(4).fill(playerAddress)
        
        console.log('   Calling balanceOfBatch...')
        const balances = await contract.balanceOfBatch(addresses, buffIds)
        console.log('   [OK] Balances received:', balances.map(b => b.toString()))
        
        return buffIds.map((id, index) => ({
          tokenId: id,
          balance: Number(balances[index])
        }))
      },
      { cacheDurationMs: 15000 } // AGC: Cache for 15 seconds
    )
  }

  // AGC: Get player OGB status without wallet connection (read-only)
  const getPlayerOGBStatusReadOnly = useCallback(async (playerAddress: string) => {
    // AGC: Use rate limiter with 10-second cache per player
    return executeWithRateLimit(
      `ogb-status-${playerAddress}`,
      async () => {
        console.log(' Getting player OGB status (read-only) for:', playerAddress)
        
        // AGC: Use provider for read-only operations
        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.OGB_TOKEN, OGB_TOKEN_ABI, readProvider)
        
        // AGC: Primary path – leverage aggregate status view for minimal RPC calls
        try {
          const [balance, canClaim, canBattle] = await contract.getPlayerStatus(playerAddress)
          
          const result = {
            balance: ethers.formatEther(balance),
            balanceWei: balance,
            canClaim,
            canBattle
          }
          
          console.log('[OK] Player OGB status (read-only):', result)
          return result
        } catch (primaryError: any) {
          console.warn('AGC: getPlayerStatus view call failed, falling back to manual reads', primaryError)
          
          try {
            // AGC: Fallback – call individual getters so older token builds still work
            const [balanceRaw, hasReceivedAirdrop, canBattleFallback] = await Promise.all([
              contract.balanceOf(playerAddress),
              contract.hasReceivedAirdrop(playerAddress),
              contract.canPlayBattle(playerAddress)
            ])
            
            const fallbackResult = {
              balance: ethers.formatEther(balanceRaw),
              balanceWei: balanceRaw,
              canClaim: !hasReceivedAirdrop,
              canBattle: canBattleFallback
            }
            
            console.log('AGC: Player OGB status (fallback):', fallbackResult)
            return fallbackResult
          } catch (fallbackError: any) {
            console.error('AGC: Fallback OGB status lookup failed', fallbackError)
            throw fallbackError
          }
        }
      },
      { cacheDurationMs: 10000 } // AGC: Cache for 10 seconds
    )
  }, [])

  return {
    provider,
    signer,
    isConnected,
    getCurrentPlayerAddress,
    connectWallet,
    disconnectWallet,
    addTestAccount,
    requestBattleRandomness,
    getBattleRandom,
    getAvailableBuffs,
    canUseBuffToday,
    markBuffUsed,
    getPlayerOGBStatus,
    claimAirdrop,
    canPlayBattle,
    chargeBattleCost,
    processBattleResult,
    processDrawResult,
    getTotalBurned,
    refreshOGBStatus,
    testMetaMaskConnection,
    getRngBeaconContract,
    getOGBTokenContract,
    getBuff1155Contract,
    // AGC: Marketplace functions
    getBuffPrices,
    getBuffInfo,
    purchaseBuffWithETH,
    purchaseBuffWithOGB,
    purchaseBuffWithUSDC,
    getUSDCBalance,
    // AGC: Classic Potion functions
    purchaseClassicPotionWithETH,
    purchaseClassicPotionWithOGB,
    purchaseClassicPotionWithUSDC,
    getClassicPotionPrices,
    claimUSDCFaucet,
    getPlayerBuffBalances,
    // AGC: Get player's classic potions using on-chain getPlayerPotions view (no log scanning!)
    getPlayerClassicPotions: async (playerAddress: string) =>
      executeWithRateLimit(
        `classic-potions-${playerAddress}`,
        async () => {
          try {
            if (!playerAddress) {
              console.warn('⚠️ Skipping potion lookup - player address missing')
              return []
            }

            if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
              console.warn('⚠️ Classic Potion contract not configured')
              return []
            }

            const normalizedPlayer = ethers.getAddress(playerAddress)
            const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
            const contract = new ethers.Contract(CONTRACT_ADDRESSES.CLASSIC_POTION_1155, CLASSIC_POTION_1155_ABI, readProvider)

            console.log('🧪 Loading classic potions using on-chain getPlayerPotions view...')

            // AGC: Use the new on-chain view - O(1) read, no log scanning!
            const tokenIds: bigint[] = await contract.getPlayerPotions(normalizedPlayer)

            if (!tokenIds || tokenIds.length === 0) {
              console.log('✅ Player has no classic potions')
              return []
            }

            console.log(`   📦 Found ${tokenIds.length} potion token IDs on-chain`)

            // AGC: Fetch metadata for each owned potion
            const potionData = await Promise.all(
              tokenIds.map(async (tokenIdBigInt) => {
                const tokenId = Number(tokenIdBigInt)
                try {
                  const [metadata, balance] = await Promise.all([
                    contract.getPotionMetadata(tokenId),
                    contract.balanceOf(normalizedPlayer, tokenId)
                  ])
                  const potionType = Number(metadata[0] ?? 0)
                  const rarity = Number(metadata[1] ?? 0)
                  const magnitude = Number(metadata[2] ?? 0)
                  const mintedAt = Number(metadata[3] ?? 0)

                  // AGC: Skip if balance is 0 (shouldn't happen with new contract, but safety check)
                  if (Number(balance) === 0) {
                    return null
                  }

                  return {
                    tokenId,
                    potionType,
                    rarity,
                    magnitude,
                    balance: Number(balance),
                    mintedAt
                  }
                } catch (error: any) {
                  // AGC: Silently skip potions that fail to load (contract might not have metadata yet)
                  // AGC: Only log non-CALL_EXCEPTION errors to avoid spam
                  if (error?.code !== 'CALL_EXCEPTION') {
                    console.warn(`Failed to load potion metadata for token ${tokenId}:`, error?.message || error)
                  }
                  return null
                }
              })
            )

            const validPotions = potionData
              .filter((item): item is { tokenId: number; potionType: number; rarity: number; magnitude: number; balance: number; mintedAt: number } => item !== null)
              .sort((a, b) => b.mintedAt - a.mintedAt)
              .map(({ mintedAt, ...rest }) => rest)

            console.log(`✅ Loaded ${validPotions.length} classic potions`)
            return validPotions
          } catch (error: any) {
            // AGC: Silently handle contract errors - new contract functions might not be deployed yet
            // AGC: Only log non-CALL_EXCEPTION errors to avoid spam
            if (error?.code !== 'CALL_EXCEPTION') {
              console.warn('Failed to get player classic potions:', error?.message || error)
            }
            return []
          }
        },
        { cacheDurationMs: 15000 }
      ),
    getPlayerOGBStatusReadOnly,
    // AGC: Crafting functions
    approveOGBForCrafting,
    
    // AGC: NEW - CraftMaterial1155 functions (v6.8.0)
    getMaterialBalances: async (playerAddress: string) => {
      try {
        if (!playerAddress) {
          console.warn('⚠️ Skipping material lookup - player address missing')
          return { herb: 0, crystal: 0, essence: 0, shard: 0 }
        }

        if (CONTRACT_ADDRESSES.CRAFT_MATERIAL_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ CraftMaterial1155 contract not configured')
          return { herb: 0, crystal: 0, essence: 0, shard: 0 }
        }

        const normalizedPlayer = ethers.getAddress(playerAddress)
        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFT_MATERIAL_1155, CRAFT_MATERIAL_1155_ABI, readProvider)

        const [herb, crystal, essence, shard] = await contract.getMaterialBalances(normalizedPlayer)
        
        return {
          herb: Number(herb),
          crystal: Number(crystal),
          essence: Number(essence),
          shard: Number(shard)
        }
      } catch (error: any) {
        console.warn('Failed to get material balances:', error?.message || error)
        return { herb: 0, crystal: 0, essence: 0, shard: 0 }
      }
    },

    // AGC: NEW - CraftedPotion1155 functions (v6.8.0)
    getCraftingPrices: async (userAddress: string) => {
      try {
        if (CONTRACT_ADDRESSES.CRAFTED_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          throw new Error('CraftedPotion1155 contract not configured')
        }

        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, readProvider)
        const [ogbPrice, hasDiscount, usdPriceMicro] = await contract.getCraftingPrice(userAddress)
        
        return {
          ogbPriceWei: ogbPrice as bigint,
          ogbPrice: formatEther(ogbPrice as bigint),
          hasDiscount: hasDiscount as boolean,
          usdPriceMicro: Number(usdPriceMicro),
          usdPrice: Number(usdPriceMicro) / 1_000_000
        }
      } catch (error) {
        console.error('Failed to get crafting prices:', error)
        throw error
      }
    },

    getRecipe: async (potionTypeId: number) => {
      try {
        if (CONTRACT_ADDRESSES.CRAFTED_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          throw new Error('CraftedPotion1155 contract not configured')
        }

        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, readProvider)
        const [materialAmounts, baseMagnitude, tier, name, isActive] = await contract.getRecipe(potionTypeId)
        
        return {
          materialAmounts: {
            herb: Number(materialAmounts[0]),
            crystal: Number(materialAmounts[1]),
            essence: Number(materialAmounts[2]),
            shard: Number(materialAmounts[3])
          },
          baseMagnitude: Number(baseMagnitude),
          tier: Number(tier),
          name: name as string,
          isActive: isActive as boolean
        }
      } catch (error) {
        console.error('Failed to get recipe:', error)
        throw error
      }
    },

    canCraftPotion: async (userAddress: string, potionTypeId: number) => {
      try {
        if (CONTRACT_ADDRESSES.CRAFTED_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          return { canCraft: false, missingMaterials: { herb: 0, crystal: 0, essence: 0, shard: 0 }, ogbNeeded: BigInt(0), ogbBalance: BigInt(0) }
        }

        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, readProvider)
        const [canCraft, missingMaterials, ogbNeeded, ogbBalance] = await contract.canCraft(userAddress, potionTypeId)
        
        return {
          canCraft: canCraft as boolean,
          missingMaterials: {
            herb: Number(missingMaterials[0]),
            crystal: Number(missingMaterials[1]),
            essence: Number(missingMaterials[2]),
            shard: Number(missingMaterials[3])
          },
          ogbNeeded: ogbNeeded as bigint,
          ogbBalance: ogbBalance as bigint
        }
      } catch (error) {
        console.error('Failed to check canCraft:', error)
        return { canCraft: false, missingMaterials: { herb: 0, crystal: 0, essence: 0, shard: 0 }, ogbNeeded: BigInt(0), ogbBalance: BigInt(0) }
      }
    },

    craftPotion: async (potionTypeId: number, farmPro: number) => {
      try {
        if (!signer) {
          throw new Error('AGC: Wallet not connected')
        }
        
        if (CONTRACT_ADDRESSES.CRAFTED_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          throw new Error('CraftedPotion1155 contract not configured')
        }

        const userAddress = await signer.getAddress()
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, signer)
        
        // AGC: Approve materials for burning (CraftMaterial1155 -> CraftedPotion1155)
        const materialContract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFT_MATERIAL_1155, CRAFT_MATERIAL_1155_ABI, signer)
        const isApproved = await materialContract.isApprovedForAll(userAddress, CONTRACT_ADDRESSES.CRAFTED_POTION_1155)
        
        if (!isApproved) {
          console.log('🔓 Approving materials for crafting...')
          const approveTx = await materialContract.setApprovalForAll(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, true)
          await approveTx.wait()
          console.log('✅ Materials approved for crafting')
        }
        
        // AGC: Get crafting prices and approve OGB
        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const craftedContract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, readProvider)
        const [ogbPrice] = await craftedContract.getCraftingPrice(userAddress)
        
        const ogbContract = new ethers.Contract(CONTRACT_ADDRESSES.OGB_TOKEN, OGB_TOKEN_ABI, signer)
        let currentAllowance = await ogbContract.allowance(userAddress, CONTRACT_ADDRESSES.CRAFTED_POTION_1155)
        
        // AGC: Add 20% buffer to account for price fluctuations and rounding errors
        const approvalAmount = (ogbPrice * 120n) / 100n
        
        // AGC: Always approve if current allowance is less than the buffered amount
        // AGC: This ensures we always have enough even if price changes slightly
        // AGC: Also approve if allowance is exactly equal to price (no buffer) to prevent rounding issues
        if (currentAllowance < approvalAmount || currentAllowance === ogbPrice) {
          console.log(`🔓 Approving OGB for crafting (${formatEther(approvalAmount)} OGB, needed: ${formatEther(ogbPrice)})...`)
          const approveTx = await ogbContract.approve(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, approvalAmount)
          console.log(`⏳ Approval transaction sent: ${approveTx.hash}`)
          const approveReceipt = await approveTx.wait()
          
          if (approveReceipt.status !== 1) {
            throw new Error('OGB approval transaction failed')
          }
          
          // AGC: Verify approval went through (wait a moment for blockchain to update)
          await new Promise(resolve => setTimeout(resolve, 2000))
          
          // AGC: Re-fetch both price and allowance to ensure we have latest values
          const [verifyPrice] = await craftedContract.getCraftingPrice(userAddress)
          currentAllowance = await ogbContract.allowance(userAddress, CONTRACT_ADDRESSES.CRAFTED_POTION_1155)
          
          console.log(`🔍 Verification: allowance=${formatEther(currentAllowance)}, price=${formatEther(verifyPrice)}, approvalAmount=${formatEther(approvalAmount)}`)
          
          // AGC: Verify we have at least the needed amount
          if (currentAllowance < verifyPrice) {
            throw new Error(`OGB approval insufficient: have ${formatEther(currentAllowance)}, need ${formatEther(verifyPrice)}. Please try again.`)
          }
          
          console.log(`✅ OGB approved for crafting (allowance: ${formatEther(currentAllowance)}, needed: ${formatEther(verifyPrice)})`)
        } else {
          console.log(`✅ OGB already approved (allowance: ${formatEther(currentAllowance)}, needed: ${formatEther(ogbPrice)})`)
        }
        
        // AGC: Convert FarmPro (0-1) to 0-100 scale
        const farmProScaled = Math.round(farmPro * 100)
        
        console.log(`🔮 Crafting potion type ${potionTypeId} (FarmPro: ${farmProScaled})...`)
        
        // AGC: Double-check allowance before crafting (price might have changed)
        const [finalOgbPriceWei, , ] = await craftedContract.getCraftingPrice(userAddress)
        const finalAllowance = await ogbContract.allowance(userAddress, CONTRACT_ADDRESSES.CRAFTED_POTION_1155)
        
        // AGC: Calculate 20% buffer (multiply by 120, then divide by 100)
        const finalApprovalAmount = (finalOgbPriceWei * 120n) / 100n
        
        if (finalAllowance < finalOgbPriceWei) {
          // AGC: Price changed or allowance insufficient - approve again with buffer
          console.log(`⚠️ OGB allowance insufficient (have: ${formatEther(finalAllowance)}, need: ${formatEther(finalOgbPriceWei)}), approving ${formatEther(finalApprovalAmount)}...`)
          const reapproveTx = await ogbContract.approve(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, finalApprovalAmount)
          const reapproveReceipt = await reapproveTx.wait()
          
          if (reapproveReceipt.status !== 1) {
            throw new Error('OGB re-approval transaction failed')
          }
          
          // AGC: Verify re-approval
          const newAllowance = await ogbContract.allowance(userAddress, CONTRACT_ADDRESSES.CRAFTED_POTION_1155)
          if (newAllowance < finalOgbPriceWei) {
            throw new Error(`OGB re-approval insufficient: have ${formatEther(newAllowance)}, need ${formatEther(finalOgbPriceWei)}`)
          }
          
          console.log(`✅ Re-approved OGB (new allowance: ${formatEther(newAllowance)})`)
        } else {
          console.log(`✅ OGB allowance sufficient (${formatEther(finalAllowance)} >= ${formatEther(finalOgbPriceWei)})`)
        }
        
        const tx = await contract.craftPotion(potionTypeId, farmProScaled)
        console.log('⏳ Transaction sent:', tx.hash)
        
        const receipt = await tx.wait()
        
        if (receipt.status !== 1) {
          throw new Error('Transaction reverted - check BaseScan for details')
        }
        
        console.log('✅ Potion crafted!')
        
        // AGC: Parse PotionCrafted event from receipt
        const iface = new ethers.Interface(CRAFTED_POTION_1155_ABI)
        let potionData = null
        
        for (const log of receipt.logs) {
          try {
            const parsed = iface.parseLog({ topics: [...log.topics], data: log.data })
            if (parsed?.name === 'PotionCrafted') {
              potionData = {
                tokenId: Number(parsed.args[1]),
                potionTypeId: Number(parsed.args[2]),
                rarity: Number(parsed.args[3]),
                magnitude: Number(parsed.args[4]),
                ogbPaid: parsed.args[5] as bigint
              }
              break
            }
          } catch {
            // AGC: Not a matching event, continue
          }
        }
        
        return { receipt, potionData }
      } catch (error) {
        console.error('❌ Failed to craft potion:', error)
        throw error
      }
    },

    // AGC: Get player's crafted potions
    getPlayerCraftedPotions: async (playerAddress: string) => {
      try {
        if (!playerAddress) {
          console.warn('⚠️ Skipping crafted potion lookup - player address missing')
          return []
        }

        if (CONTRACT_ADDRESSES.CRAFTED_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ CraftedPotion1155 contract not configured')
          return []
        }

        const normalizedPlayer = ethers.getAddress(playerAddress)
        const readProvider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)
        const contract = new ethers.Contract(CONTRACT_ADDRESSES.CRAFTED_POTION_1155, CRAFTED_POTION_1155_ABI, readProvider)

        const tokenIds: bigint[] = await contract.getPotionsOf(normalizedPlayer)

        if (!tokenIds || tokenIds.length === 0) {
          return []
        }

        const potionData = await Promise.all(
          tokenIds.map(async (tokenIdBigInt) => {
            const tokenId = Number(tokenIdBigInt)
            try {
              const [metadata, balance] = await Promise.all([
                contract.getPotionMetadata(tokenId),
                contract.balanceOf(normalizedPlayer, tokenId)
              ])
              
              if (Number(balance) === 0) return null

              return {
                tokenId,
                potionTypeId: Number(metadata[0]),
                rarity: Number(metadata[1]),
                magnitude: Number(metadata[2]),
                mintedAt: Number(metadata[3]),
                balance: Number(balance)
              }
            } catch {
              return null
            }
          })
        )

        return potionData
          .filter((item): item is NonNullable<typeof item> => item !== null)
          .sort((a, b) => b.mintedAt - a.mintedAt)
      } catch (error: any) {
        console.warn('Failed to get player crafted potions:', error?.message || error)
        return []
      }
    }
  }
}
